import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_digital-asset-io/artifacts/w5ymcp91_IODX%20logo%20with%20blue%20gradient%20background.png';

const navLinks = [
  { label: 'Overview', href: '#overview' },
  { label: 'Market', href: '#market' },
  { label: 'Roadmap', href: '#roadmap' },
  { label: 'Contact', href: '#contact' },
];

const navMotion = {
  initial: { y: -80 },
  animate: { y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const mobileMenuMotion = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: 'auto' },
  exit: { opacity: 0, height: 0 },
};

function DesktopNav() {
  return (
    <div className="hidden md:flex items-center gap-8">
      {navLinks.map((link) => (
        <a
          key={link.href}
          href={link.href}
          data-testid={`nav-link-${link.label.toLowerCase()}`}
          className="text-sm tracking-wide text-[#94A3B8] hover:text-[#60A5FA] transition-colors duration-300"
        >
          {link.label}
        </a>
      ))}
      <a
        href="#contact"
        data-testid="nav-cta"
        className="rounded-sm bg-[#3B82F6] px-5 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-[#60A5FA] hover:shadow-lg hover:shadow-blue-500/25"
      >
        Investor Access
      </a>
    </div>
  );
}

function MobileMenu({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="mobile-menu"
          {...mobileMenuMotion}
          className="md:hidden bg-[#060B1E]/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
        >
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={onClose}
                className="text-sm tracking-wide text-[#94A3B8] hover:text-[#60A5FA] transition-colors py-2"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={onClose}
              className="mt-2 rounded-sm bg-[#3B82F6] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white text-center"
            >
              Investor Access
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 40);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <motion.nav
      data-testid="navbar"
      {...navMotion}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#060B1E]/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/30'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <a href="#hero" className="flex items-center gap-2 group" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="IODX Logo"
              className="h-9 w-9 rounded-md object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <span className="text-sm font-bold tracking-[0.12em] uppercase text-[#F1F5F9]">
              IOD<span className="text-[#3B82F6]">X</span>
            </span>
          </a>

          <DesktopNav />

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#94A3B8] hover:text-[#60A5FA] transition-colors"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <MobileMenu open={menuOpen} onClose={handleMenuClose} />
    </motion.nav>
  );
}
